// src/components/alerts/AlertKpiStrip.js
// --- UPDATE (Alert System AI) — NEW: the six numbers a supervisor needs, all from the server
// (counts via one GROUP BY; MTTA / MTTR / today from the FlowSense AI snapshot). Unknown → "—".
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, HStack, Icon, SimpleGrid, Skeleton, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { MdNotificationsActive, MdHourglassTop, MdBolt, MdTaskAlt, MdTrendingUp, MdRule } from 'react-icons/md';
import { fmtMinutes } from './alertUtils';

const Sparkline = ({ values, color }) => {
    if (!values?.length) return null;
    const w = 72, h = 22, max = Math.max(1, ...values);
    const pts = values.map((v, i) => `${(i / Math.max(1, values.length - 1)) * w},${h - (v / max) * (h - 2) - 1}`).join(' ');
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
            <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

const Kpi = ({ label, value, sub, icon, color, tip, loading, spark }) => {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.100', 'gray.700');
    const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
    const iconColor = useColorModeValue(`${color}.500`, `${color}.200`);
    const muted = useColorModeValue('gray.500', 'gray.400');
    const sparkColor = useColorModeValue('#805AD5', '#B794F4');
    return (
        <Tooltip hasArrow label={tip} isDisabled={!tip} openDelay={300}>
            <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="2xl" p={4} boxShadow="sm" _hover={{ boxShadow: 'md', borderColor: `${color}.300` }} transition="all .15s">
                <Flex justify="space-between" align="flex-start">
                    <Box minW={0}>
                        <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide" noOfLines={1}>{label}</Text>
                        {loading ? <Skeleton h="28px" w="60px" mt={1} /> : (
                            <Text fontSize="2xl" fontWeight="800" color={iconColor} lineHeight="1.2" mt={1}>{value}</Text>
                        )}
                    </Box>
                    <Box bg={iconBg} p={2} borderRadius="lg"><Icon as={icon} color={iconColor} boxSize={5} /></Box>
                </Flex>
                <HStack justify="space-between" mt={1} minH="22px">
                    <Text fontSize="xs" color={muted} noOfLines={1}>{sub}</Text>
                    {spark && <Sparkline values={spark} color={sparkColor} />}
                </HStack>
            </Box>
        </Tooltip>
    );
};

function AlertKpiStrip({ stats, intel, loading }) {
    const a = stats?.alerts;
    const r = stats?.rules;
    const k = intel?.kpis;
    const staleLimits = intel?.limits?.stale;
    const noStats = !a;
    return (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={4} mb={6}>
            <Kpi label="Open alerts" icon={MdNotificationsActive} color="red" loading={loading && noStats}
                value={a ? a.open : '—'}
                sub={a ? `${a.openBySeverity?.critical ?? 0} critical · ${a.openBySeverity?.high ?? 0} high` : 'Loading…'}
                tip="Active + acknowledged + work order created" />
            <Kpi label="Waiting for ack" icon={MdHourglassTop} color={k?.staleCount ? 'orange' : 'green'} loading={loading && !k}
                value={k ? k.staleCount : '—'}
                sub={staleLimits ? `critical > ${staleLimits.critical} min · high > ${staleLimits.high} min` : 'FlowSense AI'}
                tip="Active alerts nobody acknowledged within the limit. Critical ones are escalated to supervisors automatically." />
            <Kpi label="Time to acknowledge" icon={MdBolt} color="blue" loading={loading && !k}
                value={fmtMinutes(k?.mttaMin)} sub="average · last 30 days" tip="MTTA: from alert raised to acknowledged" />
            <Kpi label="Time to resolve" icon={MdTaskAlt} color="teal" loading={loading && !k}
                value={fmtMinutes(k?.mttrMin)} sub={k?.autoResolvedPct !== null && k?.autoResolvedPct !== undefined ? `${k.autoResolvedPct}% cleared on their own` : 'average · last 30 days'}
                tip="MTTR: from alert raised to resolved (30 days)" />
            <Kpi label="Alerts today" icon={MdTrendingUp} color={intel?.storm?.storm ? 'red' : 'purple'} loading={loading && !k}
                value={k ? k.today : '—'}
                sub={k?.usualDay !== null && k?.usualDay !== undefined ? `usual day ≈ ${k.usualDay}` : '14-day trend'}
                spark={intel?.trend?.map(t => t.count)}
                tip={intel?.storm?.storm ? `Alert storm: today is ${intel.storm.z ?? '∞'}σ above the usual day` : 'Last 14 days'} />
            <Kpi label="Rules enabled" icon={MdRule} color="green" loading={loading && noStats}
                value={r ? `${r.enabled}/${r.total}` : '—'}
                sub={r ? `${r.critical} critical · ${r.high} high rules` : ''} />
        </SimpleGrid>
    );
}

AlertKpiStrip.propTypes = { stats: PropTypes.object, intel: PropTypes.object, loading: PropTypes.bool };

export default React.memo(AlertKpiStrip);
// --- END UPDATE
